<template lang="pug">
  div.default-modal
    .modal-header
      .modal-title Custom Params Helper
      a.close(href="javascript:void(0);", @click="$emit('close')")
        i.la.la-times
    form(@submit.prevent="save")
      .modal-body
        .modal-body-inner
          .form-group
            label Keys (columns)
            // Columns schema (order matters for UI + serialization)
            .d-flex.flex-wrap
              span.small-label.label-primary.mr-1.mb-1(v-for="(m, idx) in table.columnOrder", :key="m")
                span {{ m }}
                a.ml-1(href="javascript:void(0);", @click="removeMacro(idx)", v-b-tooltip.hover.top, title="Remove", style="color: inherit; opacity: 0.85;")
                  i.la.la-times

            // Macro picker (type-to-search; don't render full list by default)
            .d-flex.align-items-center.mt-2(style="gap: 8px;")
              input.form-control.form-control-sm(
                type="text"
                placeholder="Enter key (e.g. sub1)"
                v-model="newColumnKey"
                style="min-width: 180px; max-width: 220px;"
                @keydown.enter.prevent="commitNewColumns()"
              )
              button.btn.btn-primary(type="button", @click="commitNewColumns()", :disabled="!canAddColumn")
                i.la.la-plus

          .form-group
            .d-flex.justify-content-between.align-items-center
              label.mb-0 Rows (values)
              button.btn.btn-secondary.btn-sm(type="button", @click="addRow()", :disabled="!table.columnOrder.length")
                i.la.la-plus
                |  Add row

            .table-responsive.mt-2
              // Each row serializes into one querystring line
              div(v-if="!table.columnOrder.length")
                span.text-muted Add a key (column) to start.

              table.table.table-bordered.table-sm(v-else)
                thead
                  tr
                    th(v-for="m in table.columnOrder", :key="m") {{ m }}
                    th.text-center(style="width: 52px") &nbsp;
                tbody
                  tr(v-for="(r, rowIdx) in table.rows", :key="rowIdx")
                    td(v-for="m in table.columnOrder", :key="m")
                      input.form-control.form-control-sm(
                        type="text"
                        :value="(table.rows[rowIdx] && table.rows[rowIdx][m]) || ''"
                        @input="setCell(rowIdx, m, $event.target.value)"
                      )
                    td.text-center
                      button.btn.btn-default.btn-circle.btn-sm(type="button", @click="removeRow(rowIdx)", v-b-tooltip.hover.right, title="Remove row")
                        i.la.la-times

                tbody(v-if="!table.rows.length")
                  tr
                    td(:colspan="table.columnOrder.length + 1")
                      span.text-muted No rows yet. Click “Add row”.

      .modal-footer
        button.btn.btn-secondary(type="button", @click="$emit('close')") Cancel
        button.btn.btn-primary(type="submit")
          i.la.la-check
          |  Apply
</template>

<script>
// @ts-nocheck
// Modal returns value via localStorage + before-close hook (project pattern)
const STORAGE_KEY = 'customParamsHelper';

function safeDecode(s) {
  try {
    return decodeURIComponent(s);
  } catch (e) {
    return s;
  }
}

function normalizeLine(line) {
  const trimmed = (line || '').trim();
  if (!trimmed) return '';
  let l = trimmed;
  if (l.startsWith('?')) l = l.slice(1);
  if (l.startsWith('&')) l = l.slice(1);
  return l;
}

function parseLineToPairs(line) {
  const normalized = normalizeLine(line);
  if (!normalized) return [];
  const parts = normalized.split('&').filter(Boolean);
  const pairs = [];
  for (const part of parts) {
    const eqIdx = part.indexOf('=');
    if (eqIdx === -1) {
      pairs.push({ k: safeDecode(part).trim(), v: '' });
      continue;
    }
    const k = safeDecode(part.slice(0, eqIdx)).trim();
    const v = safeDecode(part.slice(eqIdx + 1));
    if (!k) continue;
    pairs.push({ k, v });
  }
  return pairs;
}

function parseCustomParams(value) {
  const raw = value || '';
  const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const useLeadingAmpersand = lines.some((l) => l.startsWith('&'));

  const columnOrder = [];
  const rows = [];

  for (const line of lines) {
    const pairs = parseLineToPairs(line);
    if (!pairs.length) continue;

    const row = {};
    for (const { k, v } of pairs) {
      if (!columnOrder.includes(k)) columnOrder.push(k);
      row[k] = v;
    }
    rows.push(row);
  }

  return { columnOrder, rows, useLeadingAmpersand };
}

// Preserve macro placeholders (e.g. `{clickid}`) as literal text
function encodeValuePreservingMacros(v) {
  return String(v)
    .split(/(\{[^{}]+\})/)
    .map((part) => (/^\{[^{}]+\}$/.test(part) ? part : encodeURIComponent(part)))
    .join('');
}

function serializeCustomParams(table, opts) {
  const t = table || { columnOrder: [], rows: [] };
  const columnOrder = Array.isArray(t.columnOrder) ? t.columnOrder : [];
  const rows = Array.isArray(t.rows) ? t.rows : [];
  const useLeadingAmpersand = !!(opts && opts.useLeadingAmpersand);

  const lines = [];
  for (const row of rows) {
    const parts = [];
    for (const key of columnOrder) {
      const v = row && row[key] !== undefined && row[key] !== null ? String(row[key]) : '';
      if (v === '') continue;
      parts.push(`${key}=${encodeValuePreservingMacros(v)}`);
    }
    if (!parts.length) continue;
    lines.push((useLeadingAmpersand ? '&' : '') + parts.join('&'));
  }
  return lines.join('\n');
}

export default {
  name: 'CustomParamsHelperModal',
  props: {
    value: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      // Canonical UI state: schema (columnOrder) + data (rows)
      table: {
        columnOrder: [],
        rows: [],
      },
      newColumnKey: '',
      /** preserve legacy formatting where lines start with "&" */
      useLeadingAmpersand: false,
    };
  },
  computed: {
    canAddColumn() {
      const raw = (this.newColumnKey || '').trim();
      if (!raw) return false;
      const keys = raw.split(',').map((s) => s.trim()).filter(Boolean);
      if (!keys.length) return false;
      return keys.some((k) => this.isValidColumnKey(k) && !this.table.columnOrder.includes(k));
    },
  },
  mounted() {
    // Hydrate UI state from querystring-like input
    this.hydrateFromValue(this.value);
  },
  methods: {
    apply(action) {
      // Single mutation entrypoint (copy-on-write)
      const current = this.table || { columnOrder: [], rows: [] };
      const next = {
        columnOrder: Array.isArray(current.columnOrder) ? current.columnOrder.slice() : [],
        rows: Array.isArray(current.rows) ? current.rows.map((r) => ({ ...(r || {}) })) : [],
      };

      if (action && action.type === 'setCell') {
        const rowIdx = action.rowIdx;
        const key = action.key;
        const val = action.val;
        if (next.rows[rowIdx]) next.rows[rowIdx][key] = val;
      } else if (action && action.type === 'addColumn') {
        const key = action.key;
        if (key && !next.columnOrder.includes(key)) next.columnOrder.push(key);
      } else if (action && action.type === 'removeColumn') {
        const idx = action.idx;
        const key = next.columnOrder[idx];
        if (key !== undefined) {
          next.columnOrder.splice(idx, 1);
          next.rows.forEach((r) => {
            if (r && Object.prototype.hasOwnProperty.call(r, key)) delete r[key];
          });
        }
      } else if (action && action.type === 'addRow') {
        next.rows.push({});
      } else if (action && action.type === 'removeRow') {
        const idx = action.idx;
        if (idx >= 0 && idx < next.rows.length) next.rows.splice(idx, 1);
      }

      if (!next.columnOrder.length) next.columnOrder.push(this.getFallbackColumn());
      if (!next.rows.length) next.rows.push({});

      this.table = next;
    },
    getFallbackColumn() {
      // Default column when input is empty / all removed
      return 'sub1';
    },
    hydrateFromValue(value) {
      const parsed = parseCustomParams(value);
      this.useLeadingAmpersand = !!parsed.useLeadingAmpersand;

      const columnOrder = (parsed.columnOrder || []).slice();
      const rows = (parsed.rows || []).slice();

      if (!columnOrder.length) columnOrder.push(this.getFallbackColumn());
      if (!rows.length) rows.push({});

      this.table = { columnOrder, rows };
    },
    setCell(rowIdx, key, val) {
      this.apply({ type: 'setCell', rowIdx, key, val });
    },
    isValidColumnKey(k) {
      const s = (k || '').trim();
      if (!s) return false;
      if (s.includes('&') || s.includes('=') || s.includes('?') || s.includes(' ')) return false;
      return true;
    },
    commitNewColumns() {
      const raw = (this.newColumnKey || '').trim();
      if (!raw) return;
      const keys = raw.split(',').map((s) => s.trim()).filter(Boolean);
      if (!keys.length) return;

      let addedAny = false;
      for (const k of keys) {
        if (!this.isValidColumnKey(k)) continue;
        if (this.table.columnOrder.includes(k)) continue;
        this.apply({ type: 'addColumn', key: k });
        addedAny = true;
      }

      if (addedAny) this.newColumnKey = '';
    },
    removeMacro(idx) {
      this.apply({ type: 'removeColumn', idx });
    },
    addRow() {
      this.apply({ type: 'addRow' });
    },
    removeRow(idx) {
      this.apply({ type: 'removeRow', idx });
    },
    serialize() {
      return serializeCustomParams(this.table, { useLeadingAmpersand: this.useLeadingAmpersand });
    },
    save() {
      const out = this.serialize();
      localStorage.setItem(STORAGE_KEY, out);
      this.$emit('close');
    },
  },
};
</script>
